import i18next from 'i18next';

/**
 * 短信服务错误码常量
 * 用于统一管理短信相关的错误码和错误消息
 */

// 短信错误码枚举
export enum SmsErrorCode {
  // 手机号发送短信次数超出限制
  LIMIT_MOBILE_DAILY = 600003,
  // IP发送短信次数超出限制
  LIMIT_IP_DAILY = 600004,
  // 发送短信失败
  SEND_FAILED = 600005,
  // 验证码错误
  INVALID_SMS_CODE = 600006,
  // 未获取验证码
  SMS_NOT_REQUESTED = 600007,
  // 必须填写手机号
  CODE_MUST_FILL_MOBILE = 600008,
  // 手机号已绑定其他用户
  CODE_MOBILE_ALREADY_EXISTS = 600009,
  // 微信号已绑定其他用户
  CODE_WECHAT_ALREADY_EXISTS = 600012,
}

// 短信错误消息对应的 i18n 翻译键
const SMS_ERROR_MESSAGE_KEYS: Record<SmsErrorCode, string> = {
  [SmsErrorCode.LIMIT_MOBILE_DAILY]: 'error.smsLimitMobileDaily',
  [SmsErrorCode.LIMIT_IP_DAILY]: 'error.smsLimitIpDaily',
  [SmsErrorCode.SEND_FAILED]: 'error.smsSendFailed',
  [SmsErrorCode.INVALID_SMS_CODE]: 'error.smsInvalidCode',
  [SmsErrorCode.SMS_NOT_REQUESTED]: 'error.smsNotRequested',
  [SmsErrorCode.CODE_MUST_FILL_MOBILE]: 'error.smsMustFillMobile',
  [SmsErrorCode.CODE_MOBILE_ALREADY_EXISTS]: 'error.smsMobileAlreadyExists',
  [SmsErrorCode.CODE_WECHAT_ALREADY_EXISTS]: 'error.smsWechatAlreadyExists',
};

// 短信错误类型分类
export const SMS_ERROR_TYPES = {
  // 频率限制类错误（不可重试）
  RATE_LIMIT: [SmsErrorCode.LIMIT_MOBILE_DAILY, SmsErrorCode.LIMIT_IP_DAILY],
  // 发送失败类错误（可重试）
  SEND_FAILURE: [SmsErrorCode.SEND_FAILED],
  // 验证码相关错误（需要用户重新操作）
  VALIDATION_ERROR: [
    SmsErrorCode.INVALID_SMS_CODE,
    SmsErrorCode.SMS_NOT_REQUESTED,
    SmsErrorCode.CODE_MUST_FILL_MOBILE,
    SmsErrorCode.CODE_MOBILE_ALREADY_EXISTS,
  ],
} as const;

/**
 * 检查是否为短信相关错误码
 * @param code 错误码
 * @returns 是否为短信错误码
 */
export const isSmsErrorCode = (code: number): code is SmsErrorCode => {
  return Object.values(SmsErrorCode).includes(code as SmsErrorCode);
};

/**
 * 获取短信错误消息
 * @param code 错误码
 * @returns 错误消息
 */
export const getSmsErrorMessage = (code: number): string => {
  if (isSmsErrorCode(code)) {
    const key = SMS_ERROR_MESSAGE_KEYS[code];
    if (key) {
      return i18next.t(key);
    }
  }
  return i18next.t('error.smsServiceError');
};

/**
 * 检查错误是否可重试
 * @param code 错误码
 * @returns 是否可重试
 */
export const isSmsErrorRetryable = (code: number): boolean => {
  if (!isSmsErrorCode(code)) {
    return true; // 非短信错误码默认可重试
  }

  return SMS_ERROR_TYPES.SEND_FAILURE.includes(code as SmsErrorCode.SEND_FAILED);
};

/**
 * 检查是否为验证码相关错误
 * @param code 错误码
 * @returns 是否为验证码相关错误
 */
export const isSmsValidationError = (code: number): boolean => {
  if (!isSmsErrorCode(code)) {
    return false;
  }

  return SMS_ERROR_TYPES.VALIDATION_ERROR.includes(
    code as
      | SmsErrorCode.INVALID_SMS_CODE
      | SmsErrorCode.SMS_NOT_REQUESTED
      | SmsErrorCode.CODE_MUST_FILL_MOBILE
      | SmsErrorCode.CODE_MOBILE_ALREADY_EXISTS
  );
};

/**
 * 检查是否为频率限制错误
 * @param code 错误码
 * @returns 是否为频率限制错误
 */
export const isSmsRateLimitError = (code: number): boolean => {
  if (!isSmsErrorCode(code)) {
    return false;
  }

  return SMS_ERROR_TYPES.RATE_LIMIT.includes(
    code as SmsErrorCode.LIMIT_MOBILE_DAILY | SmsErrorCode.LIMIT_IP_DAILY
  );
};

// 导出所有常量
export default {
  SmsErrorCode,
  SMS_ERROR_TYPES,
  isSmsErrorCode,
  getSmsErrorMessage,
  isSmsErrorRetryable,
  isSmsRateLimitError,
  isSmsValidationError,
};
